import type { ButtonHTMLAttributes, ReactNode } from "react";
import Link from "next/link";
import { EclipseWaiting } from "@/components/shell/Logo";
import { cn, DISABLED, FOCUS, PRESS } from "./cn";

export type ButtonVariant =
  | "primary"
  | "accent"
  | "secondary"
  | "soft"
  | "outline"
  | "destructive"
  | "danger"
  | "ghost"
  | "text";
export type ButtonSize = "lg" | "md" | "sm" | "pill";

// Release 1 · Components §01. Amber is THE action (one per screen); `accent`
// is an alias of primary. `secondary` (= `outline`) is the ghost pill: 1px
// cream at 30%, firming to 60% on hover, any number per screen.
// `destructive` is the cream-filled confirm — only inside a dialog or sheet
// that has stated the cost first. `danger` is kept for callers and now draws
// as a cream ring (coral is retired). `text` (= `ghost`) is the escape hatch:
// 13/500 at 72%, still a 44px hit area. Everything is a pill.
const VARIANT: Record<ButtonVariant, string> = {
  primary: "bg-gold text-on-action hover:brightness-105 active:brightness-[0.92]",
  accent: "bg-gold text-on-action hover:brightness-105 active:brightness-[0.92]",
  secondary: "border border-line-strong bg-transparent text-ink hover:border-ink/60 active:bg-ink/5",
  outline: "border border-line-strong bg-transparent text-ink hover:border-ink/60 active:bg-ink/5",
  soft: "bg-ink/12 text-ink hover:bg-ink/20",
  destructive: "bg-ink text-on-ink hover:brightness-105 active:brightness-[0.92]",
  danger: "border border-danger/50 bg-transparent text-danger hover:border-danger",
  ghost: "bg-transparent text-muted hover:text-ink",
  text: "bg-transparent text-muted hover:text-ink",
};

// Box only — height, padding, radius. Type is chosen separately so two
// font-size utilities never land on the same element.
// Targets (a11y pass): primary/secondary 52px on phones, 44px from md (desk).
const SIZE: Record<ButtonSize, string> = {
  lg: "min-h-[52px] rounded-full px-7 py-4 md:min-h-11 md:py-3", // 52 → 44, wide CTA
  md: "min-h-[52px] rounded-full px-6 py-4 md:min-h-11 md:py-3", // 52 → 44
  sm: "min-h-11 rounded-full px-4 py-2", // 44 everywhere
  pill: "min-h-11 rounded-full px-6 py-2", // 44, wider — inline CTAs
};

const QUIET = new Set<ButtonVariant>(["ghost", "text"]);

function type(variant: ButtonVariant, size: ButtonSize) {
  if (QUIET.has(variant)) return "text-[13px] font-medium";
  return size === "sm" || size === "pill" ? "text-[13px] font-semibold" : "text-[15px] font-semibold";
}

type Common = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  block?: boolean;
  leading?: ReactNode;
  trailing?: ReactNode;
  /** Shows the Eclipse drift in place of `leading`, sets aria-busy and
   *  blocks presses. Nothing spins. */
  loading?: boolean;
  className?: string;
  children: ReactNode;
};

export type ButtonProps = Common &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children" | "className"> & { href?: never };

export type ButtonLinkProps = Common & { href: string; target?: string; disabled?: boolean };

function classes({
  variant = "primary",
  size = "md",
  block,
  disabled,
  loading,
  className,
}: Common & { disabled?: boolean }) {
  return cn(
    "inline-flex cursor-pointer items-center justify-center gap-2 leading-none whitespace-nowrap",
    VARIANT[variant],
    SIZE[size],
    type(variant, size),
    block && "w-full",
    disabled && DISABLED,
    loading && "pointer-events-none",
    FOCUS,
    PRESS,
    className,
  );
}

export function Button(props: ButtonProps | ButtonLinkProps) {
  const { variant, size, block, leading, trailing, loading, className, children } = props;
  const body = (
    <>
      {loading ? <EclipseWaiting size={18} tone="current" label="Working" /> : leading}
      <span>{children}</span>
      {trailing}
    </>
  );

  if ("href" in props && props.href !== undefined) {
    const { href, target, disabled } = props;
    return (
      <Link
        href={href}
        target={target}
        aria-disabled={disabled || undefined}
        aria-busy={loading || undefined}
        className={classes({ variant, size, block, disabled, loading, className, children })}
      >
        {body}
      </Link>
    );
  }

  // Strip the presentational props so only real DOM attributes reach <button>.
  const rest = { ...(props as ButtonProps) } as Partial<ButtonProps> &
    ButtonHTMLAttributes<HTMLButtonElement>;
  for (const key of ["variant", "size", "block", "leading", "trailing", "loading", "className", "children", "href"] as const) {
    delete (rest as Record<string, unknown>)[key];
  }
  return (
    <button
      {...rest}
      type={rest.type ?? "button"}
      disabled={rest.disabled || loading || undefined}
      aria-busy={loading || undefined}
      className={classes({ variant, size, block, disabled: rest.disabled, loading, className, children })}
    >
      {body}
    </button>
  );
}

/** Components §01 "Button pair": pass the primary FIRST. Phone: stacked,
 *  primary on top, both full width. Desk (md+): inline, primary on the end
 *  side. */
export function ButtonPair({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn("flex flex-col gap-3 md:flex-row-reverse md:items-center md:justify-start [&>*]:w-full md:[&>*]:w-auto", className)}>
      {children}
    </div>
  );
}
